// File: /opt/dev_environment/assets/workspace/app-init/src/pages/VerbenPage/VerbenPage.js

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useVocabulary } from '../../context/VocabularyContext';
import {
  VERB_GROUPS,
  CONJUGATION_FORMS,
  PERSONS,
  calculateCardCount,
  generateVerbCards,
  getVerbCardKey,
} from '../../constants/verbData';
import VerbCardForm from './VerbCardForm';
import StagedVerbsSection from './StagedVerbsSection';
import './VerbenPage.css';

function VerbenPage() {
  const {
    verbCards,
    isLoaded,
    addVerbCards,
    addVerbCard,
    updateVerbCard,
    deleteVerbCard,
  } = useVocabulary();

  // Tab state: 'own' (new staged verbs) | 'static' (existing generation) | 'cards' (card list)
  const [activeTab, setActiveTab] = useState('own');

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ newCount: 0, duplicateCount: 0 });
  const [successMessage, setSuccessMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const successTimerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterVerb, setFilterVerb] = useState('all');
  const [filterForm, setFilterForm] = useState('all');
  const [tooltipCardId, setTooltipCardId] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [deleteCardData, setDeleteCardData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const tooltipRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (tooltipCardId === null) return;
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltipCardId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [tooltipCardId]);

  const existingCardKeys = useMemo(() => {
    return new Set(verbCards.map((c) => getVerbCardKey(c)));
  }, [verbCards]);

  const handleGroupToggle = useCallback((groupValue) => {
    setSelectedGroups((prev) =>
      prev.includes(groupValue)
        ? prev.filter((g) => g !== groupValue)
        : [...prev, groupValue]
    );
  }, []);

  const handleFormToggle = useCallback((formValue) => {
    setSelectedForms((prev) =>
      prev.includes(formValue)
        ? prev.filter((f) => f !== formValue)
        : [...prev, formValue]
    );
  }, []);

  const previewCount = useMemo(() => {
    if (selectedGroups.length === 0 || selectedForms.length === 0) return 0;
    return calculateCardCount(selectedGroups, selectedForms);
  }, [selectedGroups, selectedForms]);

  const canGenerate = selectedGroups.length > 0 && selectedForms.length > 0;

  const generationAnalysis = useMemo(() => {
    if (!canGenerate) return { newCards: [], duplicateCount: 0, totalCount: 0 };

    const allPotentialCards = generateVerbCards(selectedGroups, selectedForms);
    const newCards = [];
    let duplicateCount = 0;

    allPotentialCards.forEach((cardData) => {
      const key = getVerbCardKey(cardData);
      if (existingCardKeys.has(key)) {
        duplicateCount++;
      } else {
        newCards.push(cardData);
      }
    });

    return { newCards, duplicateCount, totalCount: allPotentialCards.length };
  }, [canGenerate, selectedGroups, selectedForms, existingCardKeys]);

  const allAreDuplicates = canGenerate && generationAnalysis.newCards.length === 0 && generationAnalysis.duplicateCount > 0;
  const hasPartialDuplicates = canGenerate && generationAnalysis.newCards.length > 0 && generationAnalysis.duplicateCount > 0;

  const handleGenerateClick = useCallback(async () => {
    if (!canGenerate) return;

    if (allAreDuplicates) {
      setDuplicateInfo({
        newCount: 0,
        duplicateCount: generationAnalysis.duplicateCount,
      });
      setShowDuplicateWarning(true);
      return;
    }

    await doGenerate(generationAnalysis.newCards);
  }, [canGenerate, allAreDuplicates, generationAnalysis]);

  const doGenerate = useCallback(async (cardsToAdd) => {
    if (cardsToAdd.length === 0) return;

    setIsGenerating(true);
    try {
      await addVerbCards(cardsToAdd);

      const skippedMsg = generationAnalysis.duplicateCount > 0
        ? ` (${generationAnalysis.duplicateCount} Duplikate übersprungen)`
        : '';

      setSuccessMessage(`${cardsToAdd.length} Verbkarten wurden erstellt!${skippedMsg}`);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        setSuccessMessage('');
      }, 4000);

      setSelectedGroups([]);
      setSelectedForms([]);
      setShowDuplicateWarning(false);
    } catch (error) {
      console.error('Failed to generate verb cards:', error);
      setSuccessMessage('');
    } finally {
      setIsGenerating(false);
    }
  }, [addVerbCards, generationAnalysis.duplicateCount]);

  const handleDuplicateCancel = useCallback(() => {
    setShowDuplicateWarning(false);
  }, []);

  const verbGroupCoverage = useMemo(() => {
    const hasTop10 = verbCards.some((c) => c.verbGroup === 'top10');
    const hasTop11_20 = verbCards.some((c) => c.verbGroup === 'top11_20');
    if (hasTop10 && hasTop11_20) return 'Du lernst die Top 20 der häufigsten italienischen Verben';
    if (hasTop10) return 'Du lernst die Top 10 der häufigsten italienischen Verben';
    if (hasTop11_20) return 'Du lernst die Top 11–20 der häufigsten italienischen Verben';
    return null;
  }, [verbCards]);

  const uniqueVerbs = useMemo(() => {
    const verbs = new Set(verbCards.map((c) => c.baseVerb));
    return ['all', ...Array.from(verbs).sort()];
  }, [verbCards]);

  const uniqueForms = useMemo(() => {
    const forms = new Set(verbCards.map((c) => c.conjugationForm));
    return ['all', ...Array.from(forms)];
  }, [verbCards]);

  const filteredVerbCards = useMemo(() => {
    let filtered = verbCards;

    if (filterVerb !== 'all') {
      filtered = filtered.filter((card) => card.baseVerb === filterVerb);
    }

    if (filterForm !== 'all') {
      filtered = filtered.filter((card) => card.conjugationForm === filterForm);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((card) =>
        (card.baseVerb || '').toLowerCase().includes(query) ||
        (card.conjugatedAnswer || '').toLowerCase().includes(query) ||
        (card.germanConjugation && card.germanConjugation.toLowerCase().includes(query)) ||
        (card.germanInfinitive && card.germanInfinitive.toLowerCase().includes(query)) ||
        (card.targetFormDescription || '').toLowerCase().includes(query) ||
        (card.conjugationFormLabel || '').toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      const verbCmp = (a.baseVerb || '').localeCompare(b.baseVerb || '', 'it');
      if (verbCmp !== 0) return verbCmp;
      return (a.targetFormDescription || '').localeCompare(b.targetFormDescription || '', 'de');
    });
  }, [verbCards, filterVerb, filterForm, searchQuery]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setTooltipCardId(null);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleInfoClick = useCallback((cardId) => {
    setTooltipCardId((prev) => (prev === cardId ? null : cardId));
  }, []);

  const handleEditClick = useCallback((card) => {
    setTooltipCardId(null);
    setEditCard(card);
  }, []);

  const handleEditSave = useCallback(async (formData) => {
    if (!editCard) return;
    try {
      await updateVerbCard(editCard.id, {
        targetPersonLabel: formData.targetPersonLabel,
        targetFormDescription: formData.targetFormDescription,
        conjugatedAnswer: formData.conjugatedAnswer,
        germanConjugation: formData.germanConjugation || editCard.germanConjugation,
      });
      setEditCard(null);
    } catch (error) {
      console.error('Failed to update verb card:', error);
    }
  }, [editCard, updateVerbCard]);

  const handleDeleteClick = useCallback((card) => {
    setTooltipCardId(null);
    setDeleteCardData(card);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteCardData) return;
    try {
      await deleteVerbCard(deleteCardData.id);
      setDeleteCardData(null);
    } catch (error) {
      console.error('Failed to delete verb card:', error);
    }
  }, [deleteCardData, deleteVerbCard]);

  const handleAddSave = useCallback(async (formData) => {
    try {
      await addVerbCard(formData);
      setShowAddDialog(false);
    } catch (error) {
      console.error('Failed to add verb card:', error);
    }
  }, [addVerbCard]);

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getFormLabel = (formValue) => {
    const form = CONJUGATION_FORMS.find((f) => f.value === formValue);
    return form ? form.label : formValue;
  };

  const hasSearchQuery = searchQuery.trim() !== '';

  if (!isLoaded) {
    return (
      <div className="verben-page">
        <div className="verben-page__empty">
          <span className="verben-page__empty-icon" role="img" aria-hidden="true">⏳</span>
          <p className="verben-page__empty-text">Laden…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verben-page">
      <div className="verben-page__header">
        <h2 className="verben-page__title">
          <span className="verben-page__title-icon" role="img" aria-hidden="true">🔤</span>
          Verben-Trainer
        </h2>
        <p className="verben-page__subtitle">
          Italienische Verbkonjugationen lernen
        </p>
      </div>

      {verbGroupCoverage && (
        <div className="verben-progress">
          <span className="verben-progress__icon" role="img" aria-hidden="true">📊</span>
          {verbGroupCoverage}
        </div>
      )}

      {/* ---- Tab Switcher ---- */}
      <div className="verben-filter-bar" style={{ marginBottom: 0 }}>
        <button
          className={`verben-filter-btn ${activeTab === 'own' ? 'verben-filter-btn--active' : ''}`}
          type="button"
          onClick={() => setActiveTab('own')}
          style={{ flex: 1, textAlign: 'center' }}
        >
          ✏️ Eigene Verben
        </button>
        <button
          className={`verben-filter-btn ${activeTab === 'static' ? 'verben-filter-btn--active' : ''}`}
          type="button"
          onClick={() => setActiveTab('static')}
          style={{ flex: 1, textAlign: 'center' }}
        >
          ⚙️ Top-Verben
        </button>
        <button
          className={`verben-filter-btn ${activeTab === 'cards' ? 'verben-filter-btn--active' : ''}`}
          type="button"
          onClick={() => setActiveTab('cards')}
          style={{ flex: 1, textAlign: 'center' }}
        >
          📋 Karten ({verbCards.length})
        </button>
      </div>

      {/* ---- Tab: Own Verbs (Staged) ---- */}
      {activeTab === 'own' && (
        <StagedVerbsSection />
      )}

      {/* ---- Tab: Static Verb Generation ---- */}
      {activeTab === 'static' && (
        <div className="verben-generation">
          <h3 className="verben-generation__title">
            <span role="img" aria-hidden="true">⚙️</span>
            Top-Verben Karten erstellen
          </h3>

          <div className="verben-generation__section">
            <div className="verben-generation__section-label">Verbgruppe</div>
            <div className="verben-chips">
              {VERB_GROUPS.map((group) => (
                <button
                  key={group.value}
                  className={`verben-chip ${selectedGroups.includes(group.value) ? 'verben-chip--selected' : ''} ${!group.enabled ? 'verben-chip--disabled' : ''}`}
                  type="button"
                  onClick={() => group.enabled && handleGroupToggle(group.value)}
                  disabled={!group.enabled}
                  aria-pressed={selectedGroups.includes(group.value)}
                >
                  {group.label}
                </button>
              ))}
            </div>
          </div>

          <div className="verben-generation__section">
            <div className="verben-generation__section-label">Konjugationsform</div>
            <div className="verben-chips">
              {CONJUGATION_FORMS.map((form) => (
                <button
                  key={form.value}
                  className={`verben-chip ${selectedForms.includes(form.value) ? 'verben-chip--selected' : ''}`}
                  type="button"
                  onClick={() => handleFormToggle(form.value)}
                  aria-pressed={selectedForms.includes(form.value)}
                >
                  {form.label}
                </button>
              ))}
            </div>
          </div>

          {canGenerate && (
            <div className="verben-preview">
              {allAreDuplicates ? (
                <>
                  <span style={{ color: 'var(--color-accent)' }}>⚠️</span>{' '}
                  Alle <span className="verben-preview__count">{generationAnalysis.totalCount}</span> Karten existieren bereits
                </>
              ) : (
                <>
                  Es werden <span className="verben-preview__count">{generationAnalysis.newCards.length}</span> neue Karten erstellt
                  {hasPartialDuplicates && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)', marginTop: '2px' }}>
                      ({generationAnalysis.duplicateCount} Duplikate werden übersprungen)
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <button
            className="verben-generate-btn"
            type="button"
            onClick={handleGenerateClick}
            disabled={!canGenerate || allAreDuplicates || isGenerating}
          >
            {isGenerating ? '⏳ Wird erstellt…' : allAreDuplicates ? '⚠️ Bereits vorhanden' : '🃏 Karten erstellen'}
          </button>

          {successMessage && (
            <div className="verben-success" role="alert">
              ✅ {successMessage}
            </div>
          )}
        </div>
      )}

      {/* ---- Tab: Card List ---- */}
      {activeTab === 'cards' && (
        <div>
          <h3 className="verben-generation__title" style={{ marginBottom: 'var(--space-sm)' }}>
            <span role="img" aria-hidden="true">📋</span>
            Verbkarten ({verbCards.length})
          </h3>

          {verbCards.length === 0 ? (
            <div className="verben-page__empty">
              <span className="verben-page__empty-icon" role="img" aria-hidden="true">📭</span>
              <p className="verben-page__empty-text">
                Noch keine Verbkarten erstellt. Füge eigene Verben hinzu oder erstelle Karten aus den Top-Verben!
              </p>
            </div>
          ) : (
            <>
              <div className="verben-search">
                <span className="verben-search__icon" aria-hidden="true">🔍</span>
                <input
                  ref={searchInputRef}
                  className="verben-search__input"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Verb, Konjugation oder Form suchen…"
                  aria-label="Verbkarten durchsuchen"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                {hasSearchQuery && (
                  <button
                    className="verben-search__clear"
                    type="button"
                    onClick={handleClearSearch}
                    aria-label="Suche zurücksetzen"
                  >
                    ✕
                  </button>
                )}
              </div>

              {hasSearchQuery && (
                <div className="verben-search__result-count" aria-live="polite">
                  {filteredVerbCards.length} von {verbCards.length} Verbkarten
                </div>
              )}

              <div className="verben-filter-bar">
                {uniqueVerbs.map((verb) => (
                  <button
                    key={verb}
                    className={`verben-filter-btn ${filterVerb === verb ? 'verben-filter-btn--active' : ''}`}
                    type="button"
                    onClick={() => setFilterVerb(verb)}
                  >
                    {verb === 'all' ? 'Alle Verben' : verb}
                  </button>
                ))}
              </div>
              <div className="verben-filter-bar">
                {uniqueForms.map((form) => (
                  <button
                    key={form}
                    className={`verben-filter-btn ${filterForm === form ? 'verben-filter-btn--active' : ''}`}
                    type="button"
                    onClick={() => setFilterForm(form)}
                  >
                    {form === 'all' ? 'Alle Formen' : getFormLabel(form)}
                  </button>
                ))}
              </div>

              {filteredVerbCards.length === 0 ? (
                <div className="verben-page__empty">
                  <span className="verben-page__empty-icon" role="img" aria-hidden="true">🔎</span>
                  <p className="verben-page__empty-text">
                    Keine Ergebnisse gefunden
                  </p>
                </div>
              ) : (
                <table className="verben-table" role="table" aria-label="Verbkarten">
                  <thead>
                    <tr>
                      <th scope="col">Info</th>
                      <th scope="col">IT Konjugation</th>
                      <th scope="col">DE Übersetzung</th>
                      <th scope="col">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVerbCards.map((card) => (
                      <tr key={card.id}>
                        <td className="verben-table__info-cell">
                          <button
                            className={`vokabeln-table__info-btn ${tooltipCardId === card.id ? 'vokabeln-table__info-btn--active' : ''}`}
                            type="button"
                            onClick={() => handleInfoClick(card.id)}
                            aria-label={`Info für ${card.baseVerb}`}
                            aria-expanded={tooltipCardId === card.id}
                          >
                            ℹ
                          </button>
                          {tooltipCardId === card.id && (
                            <div
                              className="vokabeln-tooltip"
                              ref={tooltipRef}
                              role="tooltip"
                            >
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">Verb:</span>
                                <span className="vokabeln-tooltip__value">{card.baseVerb} ({card.germanInfinitive})</span>
                              </div>
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">Zielform:</span>
                                <span className="vokabeln-tooltip__value">{card.targetFormDescription}</span>
                              </div>
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">IT:</span>
                                <span className="vokabeln-tooltip__value">{card.conjugatedAnswer}</span>
                              </div>
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">DE:</span>
                                <span className="vokabeln-tooltip__value">{card.germanConjugation || '—'}</span>
                              </div>
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">Fach:</span>
                                <span className="vokabeln-tooltip__value">{card.compartment}</span>
                              </div>
                              <div className="vokabeln-tooltip__row">
                                <span className="vokabeln-tooltip__label">Erstellt:</span>
                                <span className="vokabeln-tooltip__value">{formatDate(card.createdAt)}</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td>
                          <strong style={{ color: 'var(--color-wood-medium)' }}>{card.conjugatedAnswer}</strong>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)' }}>
                            {card.baseVerb} — {card.targetPersonLabel || 'Participio Passato'}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)' }}>
                            {card.conjugationFormLabel}
                          </div>
                        </td>
                        <td>
                          <strong>{card.germanConjugation || '—'}</strong>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)' }}>
                            {card.germanInfinitive}
                          </div>
                        </td>
                        <td className="verben-table__action-cell">
                          <button
                            className="vokabeln-table__action-btn vokabeln-table__action-btn--edit"
                            type="button"
                            onClick={() => handleEditClick(card)}
                            aria-label={`${card.baseVerb} bearbeiten`}
                          >
                            ✏️
                          </button>
                          <button
                            className="vokabeln-table__action-btn vokabeln-table__action-btn--delete"
                            type="button"
                            onClick={() => handleDeleteClick(card)}
                            aria-label={`${card.baseVerb} löschen`}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* ---- FAB for manual card add ---- */}
      {activeTab === 'cards' && (
        <button
          className="verben-fab"
          type="button"
          onClick={() => setShowAddDialog(true)}
          aria-label="Verbkarte manuell hinzufügen"
        >
          <span className="verben-fab__icon">+</span>
        </button>
      )}

      {showDuplicateWarning && (
        <div className="verben-duplicate-overlay" onClick={handleDuplicateCancel}>
          <div className="verben-duplicate-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="verben-duplicate-dialog__icon" role="img" aria-hidden="true">⚠️</div>
            <p className="verben-duplicate-dialog__message">
              Alle {duplicateInfo.duplicateCount} Karten für diese Auswahl existieren bereits.
              Es gibt keine neuen Karten zum Erstellen.
            </p>
            <div className="verben-duplicate-dialog__actions">
              <button
                className="verben-duplicate-dialog__btn verben-duplicate-dialog__btn--cancel"
                type="button"
                onClick={handleDuplicateCancel}
                style={{ flex: 1 }}
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteCardData && (
        <div className="verben-duplicate-overlay" onClick={() => setDeleteCardData(null)}>
          <div className="verben-duplicate-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="verben-duplicate-dialog__icon" role="img" aria-hidden="true">⚠️</div>
            <p className="verben-duplicate-dialog__message">
              Möchtest du diese Verbkarte wirklich löschen?
            </p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-error)', fontWeight: 'var(--font-weight-bold)' }}>
              {deleteCardData.conjugatedAnswer} ({deleteCardData.baseVerb} — {deleteCardData.targetFormDescription})
            </p>
            <div className="verben-duplicate-dialog__actions">
              <button
                className="verben-duplicate-dialog__btn verben-duplicate-dialog__btn--cancel"
                type="button"
                onClick={() => setDeleteCardData(null)}
              >
                Abbrechen
              </button>
              <button
                className="verben-duplicate-dialog__btn verben-duplicate-dialog__btn--confirm"
                type="button"
                onClick={handleDeleteConfirm}
                style={{ backgroundColor: 'var(--color-error)' }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddDialog && (
        <VerbCardForm
          title="Verbkarte hinzufügen"
          onSave={handleAddSave}
          onCancel={() => setShowAddDialog(false)}
        />
      )}

      {editCard && (
        <VerbCardForm
          title="Verbkarte bearbeiten"
          initialData={editCard}
          onSave={handleEditSave}
          onCancel={() => setEditCard(null)}
        />
      )}
    </div>
  );
}

export default VerbenPage;